import React, { createContext } from "react";
import { createSignalRContext } from "react-signalr/signalr";
import { environment } from "../config/environment";
import { OrganisationModel, CreateOrganisationModel, InvoiceExtractedResponseModel } from "@attelas/shared";
import { useAuth0 } from "@auth0/auth0-react";

const OrganisationContext = createContext<{
  organisation: OrganisationModel | null | undefined;
  isLoading: boolean;
  createOrganisation: (
    createOrganisationModel: CreateOrganisationModel
  ) => Promise<OrganisationModel | null | undefined>;
  invoiceExtractionJobs: InvoiceExtractedResponseModel[];
}>({
  organisation: null,
  isLoading: true,
  createOrganisation: () => Promise.resolve(null),
  invoiceExtractionJobs: [],
});

const OrganisationSignalRContext = createSignalRContext();

const OrganisationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [organisation, setOrganisation] = React.useState<OrganisationModel | null>(null);
  const [invoiceExtractionJobs, setInvoiceExtractionJobs] = React.useState<InvoiceExtractedResponseModel[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    setIsLoading(OrganisationSignalRContext.connection?.state !== "Connected");
  }, [OrganisationSignalRContext.connection]);

  OrganisationSignalRContext.useSignalREffect(
    "RecieveOrganisation",
    (existingOrg: OrganisationModel) => {
      if (isAuthenticated) {
        setOrganisation(existingOrg);
        setIsLoading(false);
      }
    },
    [isAuthenticated]
  );

  OrganisationSignalRContext.useSignalREffect(
    "ReceivePayableInvoiceExtractionJobs",
    (invoiceExtractionJobs: InvoiceExtractedResponseModel[]) => {
      if (isAuthenticated) {
        setInvoiceExtractionJobs(invoiceExtractionJobs);
        setIsLoading(false);
      }
    },
    [isAuthenticated]
  );

  const createOrganisation = async (createOrganisationModel: CreateOrganisationModel) => {
    return await OrganisationSignalRContext.connection?.invoke<OrganisationModel>(
      "CreateOrganisation",
      createOrganisationModel
    );
  };

  return (
    <OrganisationSignalRContext.Provider
      connectEnabled={isAuthenticated}
      accessTokenFactory={() => getAccessTokenSilently()}
      dependencies={[getAccessTokenSilently]}
      url={`${environment.apiUrl}/hub/organisation`}
    >
      <OrganisationContext.Provider value={{ organisation, isLoading, createOrganisation, invoiceExtractionJobs }}>
        {children}
      </OrganisationContext.Provider>
    </OrganisationSignalRContext.Provider>
  );
};

export { OrganisationContext, OrganisationProvider, OrganisationSignalRContext };
